import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Button, Flex, Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { useGetCommentThreadsQuery, useAddBtsCommentMutation } from "#graphql/supergraphHooks";
import { SPACING } from "#utils/spacing";
import { EmptyStateCard } from "../../../EmptyStateCard";
import { CommentAuthor, WorkflowComment } from "../../types";
import { showApiErrorToast } from "../../utils/apiErrorToast";
import { CommentsList } from "./CommentsList";
import { ComposeActions } from "./ComposeActions";
import { ComposeView } from "./ComposeView";
import * as styles from "./styles_vanilla.css";
import { adfToPlainText, formatCommentDate, htmlToPlainText } from "./utils";
import type { GetCommentThreadsQuery } from "#graphql/supergraphTypes";

type CommentsTabProps = {
  comments?: WorkflowComment[];
  loading?: boolean;
  onPost?: (body: string, visibility: "INTERNAL" | "EXTERNAL") => Promise<void>;
  issueKey?: string;
  currentUser?: CommentAuthor;
  isGPX?: boolean;
  /** BTS comment id to open and scroll to. Cleared by the parent via onTargetCommentConsumed once handled. */
  targetCommentId?: string | null;
  onTargetCommentConsumed?: () => void;
};

type ComposeState = { mode: "list" } | { mode: "compose"; isInternal: boolean };

const PREVIEW_MAX = 120;

type BackendThread = GetCommentThreadsQuery["commentThreads"]["comments"][number];
type BackendReply = BackendThread["replies"][number];

type ThreadPreviewCardProps = { thread: BackendThread; onClick: () => void; isGPX?: boolean };
const ThreadPreviewCard: React.FC<ThreadPreviewCardProps> = ({
  thread,
  onClick,
  isGPX = false,
}) => {
  const isInternal = thread.isInternal ?? false;
  const raw = adfToPlainText({ kind: "adf", document: thread.body });
  const preview = raw.length > PREVIEW_MAX ? raw.slice(0, PREVIEW_MAX) + "…" : raw || "(empty)";
  const replyCount = thread.replies?.length ?? 0;

  return (
    <button
      type="button"
      className={styles.threadPreviewCard}
      onClick={onClick}
      data-testid={`workflow-thread-preview-${thread.id}`}
    >
      <div
        className={
          isInternal ? styles.threadPreviewDotInternal : styles.threadPreviewDotExternal
        }
      />
      <div className={styles.threadPreviewContent}>
        <span className={styles.threadPreviewText}>
          <Text variant="regular-sm" color={Colors.Neutral900}>
            {preview}
          </Text>
        </span>
        <div className={styles.threadPreviewMeta}>
          {thread.taskLabel && (
            <Text variant="secondary-sm" color={Colors.Neutral600}>
              in {thread.taskLabel}
            </Text>
          )}
          {thread.taskLabel && replyCount > 0 && (
            <Text variant="secondary-sm" color={Colors.Neutral400}>
              ·
            </Text>
          )}
          {replyCount > 0 && (
            <Text variant="secondary-sm" color={Colors.Neutral600}>
              {replyCount} {replyCount === 1 ? "reply" : "replies"}
            </Text>
          )}
        </div>
      </div>
      <Flex
        flexDirection="column"
        alignItems="flex-end"
        className={styles.threadPreviewMetaFlex}
      >
        <Flex flexDirection="row" alignItems="center" gap={SPACING.X_SMALL}>
          {!isGPX && (
            <span
              className={styles.visibilityPillVariants[isInternal ? "internal" : "external"]}
              data-testid={`workflow-thread-preview-${thread.id}-visibility`}
            >
              {isInternal ? "Internal" : "External"}
            </span>
          )}
          <Text
            variant="secondary-sm"
            color={Colors.Neutral600}
            className={styles.threadPreviewAuthorName}
          >
            {thread.author.displayName ?? "Unknown"}
          </Text>
        </Flex>
        <Text
          variant="secondary-sm"
          color={Colors.Neutral400}
          className={styles.threadPreviewDate}
        >
          {formatCommentDate(thread.created)}
        </Text>
      </Flex>
    </button>
  );
};

const mapThreadToWorkflow = (thread: BackendThread): WorkflowComment[] => {
  const parent: WorkflowComment = {
    id: thread.id,
    body: { kind: "adf", document: thread.body },
    isInternal: thread.isInternal ?? false,
    parentCommentId: null,
    // taskLabel is shown as the card subject header; taskId preserved as subject id
    subject: thread.taskLabel ?? undefined,
    author: {
      name: thread.author.displayName ?? "Unknown",
      avatar: thread.author.avatarUrl ?? null,
      role: null,
    },
    createdAt: thread.created,
  };

  const replies: WorkflowComment[] = (thread.replies ?? []).map((r: BackendReply) => ({
    id: r.id,
    body: { kind: "adf", document: r.body },
    isInternal: r.isInternal ?? false,
    parentCommentId: r.parentCommentId ?? thread.id,
    author: {
      name: r.author.displayName ?? "Unknown",
      avatar: r.author.avatarUrl ?? null,
      role: null,
    },
    createdAt: r.created,
  }));

  return [parent, ...replies];
};

export const CommentsTab: React.FC<CommentsTabProps> = ({
  comments: externalComments,
  loading: externalLoading,
  onPost: externalOnPost,
  issueKey,
  currentUser,
  isGPX = false,
  targetCommentId,
  onTargetCommentConsumed,
}) => {
  const [composeState, setComposeState] = useState<ComposeState>({ mode: "list" });
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [gpxComposeKey, setGpxComposeKey] = useState(0);
  // Tracks which targetCommentId we've already scrolled to so the DOM-touching
  // effect below doesn't keep re-scrolling on every re-render of the tab.
  const scrolledForTargetIdRef = useRef<string | null>(null);

  const {
    data,
    loading: gqlLoading,
    error,
    refetch,
  } = useGetCommentThreadsQuery({
    variables: { issueKey: issueKey ?? "", includeInternal: !isGPX },
    skip: !issueKey,
    fetchPolicy: "network-only",
  });

  const [addBtsComment] = useAddBtsCommentMutation();

  // Show a toast whenever the GetCommentThreads query fails so the user
  // sees the actual API error message alongside the inline error card.
  useEffect(() => {
    if (error) showApiErrorToast(error);
  }, [error]);

  const loading = issueKey ? gqlLoading : (externalLoading ?? false);
  const threads: BackendThread[] = useMemo(
    () => (issueKey ? (data?.commentThreads?.comments ?? []) : []),
    [issueKey, data],
  );
  const openThread = threads.find((t) => t.id === openThreadId) ?? null;

  // In JSM mode: show the selected thread's comments; in external mode: use passed comments
  const displayComments: WorkflowComment[] = useMemo(
    () =>
      issueKey ? (openThread ? mapThreadToWorkflow(openThread) : []) : (externalComments ?? []),
    [issueKey, openThread, externalComments],
  );

  // When the parent requests a target comment, find its containing thread and
  // highlight that thread's preview row in the list — don't auto-drill into
  // the detail view. The user can click the preview to read the full thread.
  // Guarded by a ref so we don't re-scroll on every render for the same id.
  useEffect(() => {
    if (!targetCommentId) {
      scrolledForTargetIdRef.current = null;
      return;
    }
    if (threads.length === 0) return;
    if (scrolledForTargetIdRef.current === targetCommentId) return;
    const containingThread = threads.find(
      (t) => t.id === targetCommentId || t.replies?.some((r) => r.id === targetCommentId),
    );
    if (!containingThread) return;
    const node = document.querySelector<HTMLElement>(
      `[data-testid="workflow-thread-preview-${containingThread.id}"]`,
    );
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "center" });
    node.classList.add(styles.commentTargetHighlight);
    scrolledForTargetIdRef.current = targetCommentId;
    onTargetCommentConsumed?.();
    window.setTimeout(() => {
      node.classList.remove(styles.commentTargetHighlight);
    }, 2000);
  }, [targetCommentId, threads, onTargetCommentConsumed]);

  const handleComposeBack = useCallback(() => setComposeState({ mode: "list" }), []);

  const handleSubmit = useCallback(
    async (body: string, isInternal: boolean) => {
      if (issueKey) {
        const adfBody = {
          version: 1,
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [{ type: "text", text: htmlToPlainText(body) || " " }],
            },
          ],
        };
        await addBtsComment({
          variables: {
            issueKey,
            body: adfBody,
            isInternal,
            ...(openThreadId && { parentCommentId: openThreadId }),
          },
        });
        await refetch();
      } else if (externalOnPost) {
        await externalOnPost(body, isInternal ? "INTERNAL" : "EXTERNAL");
      }
    },
    [issueKey, openThreadId, addBtsComment, refetch, externalOnPost],
  );

  const handleGpxSubmit = useCallback(
    async (body: string) => {
      await handleSubmit(body, false);
      setGpxComposeKey((k) => k + 1);
    },
    [handleSubmit],
  );

  const showCompose = !!issueKey || !!externalOnPost;

  // Determines whether we're in the thread-list view or detail view
  const isDetailMode = !issueKey || !!openThread;

  return (
    <div className={styles.commentsTabFrame} data-testid="workflow-detail-comments-tab">
      {/* Back button shown when drilling into a thread */}
      {issueKey && openThread && (
        <Button
          variant="neutral"
          displayAsLink
          className={styles.threadDetailBackButton}
          onClick={() => {
            setOpenThreadId(null);
            setComposeState({ mode: "list" });
          }}
        >
          <Text variant="strong-sm" color={Colors.Neutral600}>
            ← All threads
          </Text>
          {openThread.taskLabel && (
            <Text variant="secondary-sm" color={Colors.Neutral600}>
              {" "}
              · {openThread.taskLabel}
            </Text>
          )}
        </Button>
      )}

      <Flex flexDirection="column" gap={SPACING.MEDIUM} className={styles.tabContentContainer}>
        {loading && (
          <div className={styles.emptyState}>
            <Text variant="regular-md" color={Colors.Neutral600}>
              Loading comments…
            </Text>
          </div>
        )}
        {!loading && error && (
          <EmptyStateCard
            icon="comment"
            title="Failed to load comments"
            subtitle="Please try again later."
            data-testid="workflow-comments-error-state"
          />
        )}

        {/* JSM Thread List */}
        {issueKey &&
          !openThread &&
          !loading &&
          !error &&
          (threads.length === 0 ? (
            <EmptyStateCard
              icon="comment"
              title="No comments to show"
              subtitle="Leave a comment for the fund admin manager"
              data-testid="workflow-comments-empty-thread-list"
            />
          ) : (
            <div className={styles.threadPreviewList}>
              {threads.map((thread) => (
                <ThreadPreviewCard
                  key={thread.id}
                  thread={thread}
                  onClick={() => setOpenThreadId(thread.id)}
                  isGPX={isGPX}
                />
              ))}
            </div>
          ))}

        {/* Thread detail / external comments */}
        {isDetailMode && !loading && !error && displayComments.length === 0 && (
          <EmptyStateCard
            icon="comment"
            title="No comments to show"
            subtitle="Leave a comment for the fund admin manager"
            data-testid="workflow-comments-empty-thread-detail"
          />
        )}
        {isDetailMode && !loading && !error && displayComments.length > 0 && (
          <CommentsList comments={displayComments} isGPX={isGPX} />
        )}
      </Flex>

      {showCompose && isGPX && (
        <ComposeView
          key={gpxComposeKey}
          isInternal={!isGPX}
          isGPX={isGPX}
          onCancel={() => setGpxComposeKey((k) => k + 1)}
          onSubmit={handleGpxSubmit}
          currentUser={currentUser}
        />
      )}

      {showCompose && !isGPX && (
        <>
          {composeState.mode === "compose" ? (
            <ComposeView
              isInternal={composeState.isInternal}
              onBack={handleComposeBack}
              onSubmit={async (body: string) => {
                await handleSubmit(body, composeState.isInternal);
                setComposeState({ mode: "list" });
              }}
              currentUser={currentUser}
            />
          ) : (
            <ComposeActions
              onPostInternal={() => setComposeState({ mode: "compose", isInternal: true })}
              onReplyToClient={() => setComposeState({ mode: "compose", isInternal: false })}
              disabled={false}
            />
          )}
        </>
      )}
    </div>
  );
};
